// src/hooks/admin/useAdminTransactions.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TransactionFilters } from '../useTransactions';
import { useTransactions } from '../useTransactions';
// AdminTransaction type alias not needed - using Transaction from useTransactions
import { useAdminApi } from '@/hooks/use-admin-api';
import { useAdminBffEnabled } from '@/hooks/useAdminBffEnabled';
import { z } from 'zod';

/**
 * useAdminTransactions — пагинация/фильтрация транзакций (админ).
 *
 * BFF:
 *   GET /api/admin/transactions?cursor=&pageSize=50&status=settled|failed|pending&q=...
 *     → 200 { items: Tx[], nextCursor?: string }
 */

const Tx = z.object({
  id: z.string().min(6),
  userId: z.string().min(3),
  kind: z.enum(['deposit', 'payout', 'fee', 'adjustment']),
  status: z.enum(['pending', 'settled', 'failed', 'canceled']),
  asset: z.string().min(2),
  amount: z.number(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
});
export type AdminTx = z.infer<typeof Tx>;

const Page = z.object({
  items: z.array(Tx),
  nextCursor: z.string().optional(),
});

export type TxFilter = {
  status?: 'pending' | 'settled' | 'failed' | 'canceled';
  q?: string;
};

// Legacy BFF-based hook (kept temporarily for internal consumers)
export function useAdminTransactionsLegacy(opts?: { pageSize?: number; initialFilter?: TxFilter }) {
  const pageSize = Math.max(10, Math.min(200, opts?.pageSize ?? 50));
  const [filter, setFilter] = useState<TxFilter>(opts?.initialFilter ?? {});
  const [items, setItems] = useState<AdminTx[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const api = useAdminApi();
  const abortRef = useRef<AbortController | null>(null);
  const { enabled: adminEnabled, loading: adminLoading } = useAdminBffEnabled();

  const buildUrl = useCallback((cur: string | null) => {
    const p = new URLSearchParams();
    p.set('pageSize', String(pageSize));
    if (filter.status) p.set('status', filter.status);
    if (filter.q) p.set('q', filter.q);
    if (cur) p.set('cursor', cur);
    return `/api/admin/transactions?${p.toString()}`;
  }, [filter.q, filter.status, pageSize]);

  const loadPage = useCallback(async (cur: string | null, append: boolean) => {
    if (!adminEnabled) {
      setLoading(adminLoading);
      if (!adminLoading) {
        setError(null);
      }
      return;
    }

    abortRef.current?.abort();
    abortRef.current = typeof AbortController !== 'undefined' ? new AbortController() : null;

    setLoading(true); setError(null);
    try {
      const url = buildUrl(cur);
      const json = await api.get(url);
      const page = Page.parse(json);
      setItems((xs) => append ? [...xs, ...page.items] : page.items);
      setCursor(cur);
      setNextCursor(page.nextCursor ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Сетевая ошибка.');
    } finally {
      setLoading(false);
    }
  }, [adminEnabled, adminLoading, api, buildUrl]);

  // initial load - only run when adminEnabled/adminLoading changes, not on loadPage changes
  useEffect(() => {
    if (!adminEnabled) {
      if (!adminLoading) {
        abortRef.current?.abort();
        setError(null);
      }
      return;
    }

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    abortRef.current = controller;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const url = buildUrl(null);
        const json = await api.get(url);
        if (!cancelled) {
          const page = Page.parse(json);
          setItems(page.items);
          setCursor(null);
          setNextCursor(page.nextCursor ?? null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Сетевая ошибка.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, [adminEnabled, adminLoading, api, buildUrl]);

  // refetch on filter change - use direct dependencies instead of loadPage
  useEffect(() => {
    if (!adminEnabled) return;

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    abortRef.current = controller;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const url = buildUrl(null);
        const json = await api.get(url);
        if (!cancelled) {
          const page = Page.parse(json);
          setItems(page.items);
          setCursor(null);
          setNextCursor(page.nextCursor ?? null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Сетевая ошибка.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, [adminEnabled, filter.status, filter.q, api, buildUrl]);

  const hasMore = useMemo(() => Boolean(nextCursor), [nextCursor]);
  const loadMore = useCallback(() => {
    if (nextCursor) return loadPage(nextCursor, true);
  }, [loadPage, nextCursor]);

  const refresh = useCallback(() => loadPage(cursor, false), [cursor, loadPage]);

  return {
    items, loading, error,
    filter, setFilter,
    hasMore, loadMore,
    refresh,
    pageSize,
  } as const;
}

// Canonical RPC-only hook (CANON §4.1)
export function useAdminTransactions(params: {
  customer_filter?: string;
  kind_filter?: string[];
  status_filter?: string[];
  currency_filter?: string;
  amount_min?: number;
  amount_max?: number;
  from_date?: string;
  to_date?: string;
  limit_param?: number;
}) {
  const filters: TransactionFilters = useMemo(() => ({
    query: params.customer_filter,
    status: params.status_filter?.[0],
    currency: params.currency_filter,
    amount_min: params.amount_min,
    amount_max: params.amount_max,
    from_date: params.from_date,
    to_date: params.to_date,
    limit: params.limit_param,
  }), [
    params.customer_filter, params.status_filter, params.currency_filter,
    params.amount_min, params.amount_max, params.from_date, params.to_date, params.limit_param,
  ]);

  const { data, isLoading, error, hasMore, loadMore, refresh, updateFilters } = useTransactions(filters);

  return {
    transactions: data,
    loading: isLoading,
    error,
    hasMore,
    loadMore,
    refresh,
    updateFilters: (nf: Partial<typeof params>) => {
      const mapped: Partial<TransactionFilters> = {
        query: nf.customer_filter,
        status: nf.status_filter?.[0],
        currency: nf.currency_filter,
        amount_min: nf.amount_min,
        amount_max: nf.amount_max,
        from_date: nf.from_date,
        to_date: nf.to_date,
        limit: nf.limit_param,
      };
      updateFilters(mapped);
    }
  } as const;
}
